import React, { useState } from 'react';
import { useCustomerTheme } from '../Theme';
import { useTranslation } from 'react-i18next';

import { Wrench } from 'lucide-react';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { chipResourceKey } from './chipResourceKey';
import { useResolvedResourceContent } from './useResolvedResourceContent';

/**
 * P3b — low-confidence routing disambiguation.
 *
 * When tri-server's router hits a genuine near-tie it emits a `toolChoices` resource
 * instead of auto-executing the marginal top tool. We render the 2-3 candidate tools as
 * one-tap pills. Tapping re-sends the original request pinned to the chosen tool's `offer`
 * (via `submitInput`'s `exclusiveToolId` option → exclusive tools filter), so the resume
 * routes deterministically. Modeled on {@link FollowUpSuggestionsResource}.
 */
interface ToolChoice {
    toolId: string;
    offer: string;
    label: string;
    description?: string;
    prompt: string;
}

interface ToolChoicesContent {
    language?: string;
    prompt?: string;
    choices: ToolChoice[];
}

interface ToolChoicesResourceProps {
    resource: {
        type: 'toolChoices';
        /** Im Stream steht statt des Inhalts nur diese Referenz. */
        reference?: string;
        data?: ToolChoicesContent;
        content?: ToolChoicesContent;
    };
    onSubmitInput?: (input: string, prompt?: string, options?: { exclusiveToolId?: string; onAccepted?: () => void }) => void;
    isLastMessage?: boolean;
    stableKey?: string;
    pendingRequest?: boolean;
}

// Persist tap state across re-renders (mirror of the follow-up component's approach).
const chosenToolChoices = new Set<string>();

export const ToolChoicesResource: React.FC<ToolChoicesResourceProps> = ({ resource, onSubmitInput, isLastMessage, stableKey, pendingRequest }) => {
    const { theme } = useCustomerTheme();
    const { t } = useTranslation();

    // Im Stream kommt nur eine Referenz an — der Inhalt wird nachgeladen.
    const content = useResolvedResourceContent<ToolChoicesContent>(resource);
    const choices = content?.choices || [];
    const prompt = content?.prompt || '';

    const resourceKey = chipResourceKey(stableKey, 'toolChoices', [
        ...choices.map((choice) => choice.toolId),
        prompt,
    ]);
    // Nicht als Lazy-Init: der Schlüssel entsteht aus dem Inhalt und ändert sich, sobald die
    // nachgeladene Referenz da ist. Ein einmal berechneter Anfangswert hinge dann am leeren Schlüssel.
    const [clicked, setClicked] = useState(false);
    const hasClicked = clicked || chosenToolChoices.has(resourceKey);

    // Only actionable on the last message and before a choice is made.
    if (!isLastMessage || hasClicked || choices.length < 2) return null;

    const handleChoiceClick = (choice: ToolChoice) => {
        // Don't process clicks while a request is pending to avoid hiding the pills
        // before submitInput can actually send the pinned resume.
        if (pendingRequest || !onSubmitInput) {
            return;
        }
        // Bubble shows the tool the user picked; the original request is re-sent, pinned to
        // the exact chosen tool (toolId, not offer) so routing is deterministic and the
        // pinned turn never re-triggers disambiguation.
        // Only mark consumed once submitInput accepts the request — otherwise a still-pending
        // prior turn makes submitInput return immediately and the pills would stay hidden
        // forever via module-level state with no way to retry.
        onSubmitInput(choice.label, choice.prompt || prompt, {
            exclusiveToolId: choice.toolId || choice.offer,
            onAccepted: () => {
                setClicked(true);
                chosenToolChoices.add(resourceKey);
            },
        });
    };

    return (
        <div className="tool-choices mb-2 mt-3">
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.5px] text-muted-foreground">
                {t('chatBotPage.chooseTool', 'Werkzeug wählen')}
            </div>

            <TooltipProvider delayDuration={300} skipDelayDuration={0}>
                <div className="flex flex-wrap items-center gap-2">
                    {choices.map((choice) => (
                        <Tooltip key={choice.toolId}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="chat-resource-tool-choice"
                                    onClick={() => handleChoiceClick(choice)}
                                    // Die Kundenfarbe kommt zur Laufzeit aus dem Theme — als CSS-Variable
                                    // gesetzt, damit der Hover-Zustand ohne JS-Stilmutation auskommt.
                                    style={{
                                        '--chip-border': `${theme.primaryLight}44`,
                                        '--chip-border-hover': theme.primaryLight,
                                    } as React.CSSProperties}
                                    className="flex max-w-[300px] items-center rounded-[20px] border border-[color:var(--chip-border)] bg-muted px-[14px] py-1.5 text-left text-foreground transition-all duration-200 hover:border-[color:var(--chip-border-hover)] hover:bg-accent hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                                >
                                    <Wrench
                                        aria-hidden
                                        className="mr-2 h-3.5 w-3.5 shrink-0"
                                        style={{ color: theme.primaryLight }}
                                    />
                                    <span className="line-clamp-2 text-[13px] leading-[1.4]">
                                        {choice.label}
                                    </span>
                                </button>
                            </TooltipTrigger>
                            <TooltipContent sideOffset={0}>
                                {choice.description || choice.label}
                            </TooltipContent>
                        </Tooltip>
                    ))}
                </div>
            </TooltipProvider>
        </div>
    );
};
